import React, { useEffect, useRef, useState } from "react";
import Table, { Column, TableHandle } from "../../../components/Table";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { Button } from "../../../components/Button";
import { FaEdit } from "react-icons/fa";
import { LightColors } from "../../../utils/colors/colors";
import { useNavigate } from "react-router-dom";

import { formattedAmount } from "../../../helper/currencyFormatter";
import { formatIndianNumber } from "../../../helper/numberFormate";

import FilterForm from "./Components/FilterForm";
import { AllProperty, setFilterData } from "../../../redux/slices/PropertyManagement/slice";
import { fetchAllProperties } from "../../../redux/slices/PropertyManagement/thunk";

const AllProperties = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const tableRef = useRef<TableHandle>(null);

  const { allPropertiesList, filterData } = useSelector(
    (state: RootState) => state.propertyManagement
  );

  const [refresh, setRefresh] = useState(false)
  const [showLoader, setShowLoader] = useState(false)

  // =============================
  // 📌 TABLE COLUMNS
  // =============================

  const columns: Column<AllProperty>[] = [
    { key: "state", label: "State", sortable: true },
    { key: "city", label: "City", sortable: true },

    {
      key: "area",
      label: "Area",
      customCell: (row: any) => <div>{row?.area?.area}</div>,
    },
    {
      key: "street",
      label: "Street",
      customCell: (row: any) => <div>{row?.street?.street}</div>,
    },

    {
      key: "surveyNumbers",
      label: "Survey Numbers",
      customCell: (row) => (
        <div>{row.surveyNumbers?.join(", ")}</div>
      ),
    },

    {
      key: "guidelineRate",
      label: "Guideline Rate",
      customCell: (row) => (
        <div>{formattedAmount(row.guidelineRate)}</div>
      ),
    },

    {
      key: "marketRate",
      label: "Market Rate Range",
      customCell: (row) => (
        <div>
          {formattedAmount(row.marketRate?.min)} -{" "}
          {formattedAmount(row.marketRate?.max)}
        </div>
      ),
    },

    {
      key: "action",
      label: "Action",
      customCell: (row) => (
        <div className="flex justify-center items-center">
          <a
            className="text-blue-600 cursor-pointer"
            onClick={() =>
              navigate(`/all-properties/edit/${row._id}`, {
                state: { row },
              })
            }
          >
            <FaEdit color={LightColors.accent} size={20} />
          </a>
        </div>
      ),
    },
  ];

  // =============================
  // 📌 ON SORT CHANGE
  // =============================
  const onSortChanged = (sortBy: any, sortOrder: any) => {
    dispatch(setFilterData({ ...filterData, sortBy, sortOrder }));
  };

  const getData = async () => {
    setShowLoader(true)
    await dispatch(fetchAllProperties({ ...filterData }));
    setShowLoader(false)
  }
  // =============================
  // 📌 FETCH DATA WHEN FILTERS CHANGE
  // =============================
  useEffect(() => {
    getData()
  }, [
    filterData.page,
    filterData.limit,
    filterData.state,
    filterData.city,
    filterData.area,
    filterData.search,
    filterData.sortBy,
    filterData.sortOrder,
    refresh
  ]);

  return (
    <div>
      <Table
        ref={tableRef}
        columns={columns as any}
        data={allPropertiesList}
        tableName="All Properties"
        totalCount={filterData.totalCount}
        currentPage={filterData.page}
        pageSize={filterData.limit}
        loading={showLoader}
        filterContent={<FilterForm tableRef={tableRef} />}

        onSearchChange={(searchText) =>
          dispatch(setFilterData({ ...filterData, search: searchText, page: 1 }))
        }

        onPageChange={(page) =>
          dispatch(setFilterData({ ...filterData, page }))
        }

        onPageSizeChange={(size) =>
          dispatch(
            setFilterData({
              ...filterData,
              limit: size,
              page: 1,
            })
          )
        }

        onSortChange={onSortChanged}
        renderRightToolItems={
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => setRefresh(!refresh)}
            >
              Refresh
            </Button>

            <Button
              variant="primary"
              onClick={() => navigate("/all-properties/add")}
            >
              Add
            </Button>
          </div>
        }
      />
    </div>
  );
};

export default AllProperties;
